import * as THREE from 'three'
import { Fragment, FunctionComponent, Suspense, useEffect, useRef, useState } from 'react'

import HospitalRoom from 'assets/gltfs/HospitalRoom2'
import Reparent from 'components/Reparent'
import FOVCamera from 'components/FOVCamera'
import PanHandler from 'components/Controls/PanHandlerComponent'
import MotionControls from 'components/Controls/MotionControls'
import ThreeJSObserve from 'components/ThreeJSObserveScene'
import ForceHierarchyUpdate from 'components/ForceHierarchyUpdate'
import { Canvas } from '@react-three/fiber'
import { _renderMatches } from 'react-router/lib/hooks'
import GenericPopup from 'components/Generic/GenericPopup'
import MoveView from 'components/moveView'
import LookHowPopup from 'components/LookHowPopup'

// this to allows use to find the right "FEEL" as preprogrammed values may not be right.
// TODO : when no longer needed in the project prune this
const dragSensitivityOverride = (() => {
  const urlParams = new URLSearchParams(window.location.search)
  let sensitivity: any = urlParams.get('DragSensitivity')
  // eslint-disable-next-line
  if (sensitivity != null) console.warn(`Sensitivity is being overwritten ${sensitivity}. TODO : remove this functionality for production}`)
  sensitivity = sensitivity ?? 1.4 // TODO : Starting putting application wide constants in once place :thinking:
  if (sensitivity instanceof String) return Number.parseFloat((sensitivity as string))
  else return sensitivity as number
})()

export type ChosenInputMethod = 'Drag' | 'Gyro' | 'Both' | 'None'

interface HospitalRoomSceneProps {
  chosenInputMethod: ChosenInputMethod
  inputInitialised: boolean
}

const startPointerPos: THREE.Vector2 = new THREE.Vector2()

const HospitalRoomScene: FunctionComponent<HospitalRoomSceneProps> = (props) => {
  const [roomReady, setRoomReady] = useState<boolean>(false)

  const [userInteracted, setUserInteracted] = useState<boolean>(false)
  const [roomAnimationState, setRoomAnimationState] = useState<number>(0)
  const [cameraTrackNode, setCameraTrackNode] = useState<THREE.Object3D | null>(null)
  const [animatingRoom, setAnimatingRoom] = useState<boolean>(false)
  const [currentCameraParent, setCurrentCameraParent] = useState<THREE.Object3D>(new THREE.Object3D())

  const [canMove, setCanMove] = useState<boolean>(false)
  const [canDragPan, setCanDragPan] = useState<boolean>(false)

  const [showBrokenLegPrompt, setShowBrokenLegPrompt] = useState<boolean>(false)
  const [showGoodLuckPrompt, setShowGoodLuckPrompt] = useState<boolean>(false)
  const [showEndPrompt, setShowEndPrompt] = useState<boolean>(false)

  const [showMoveView, setShowMoveView] = useState<boolean>(false)
  const [moveCount, setMoveCount] = useState<number>(-1)

  const [cameraModeInitialised] = useState<boolean>(false)
  const camera = useRef<THREE.Camera>(null)
  const refBlankCameraParent = useRef<THREE.Object3D | null>(null)
  const refDragCameraParent = useRef<THREE.Object3D | null>(null)
  const refGyroCameraParent = useRef<THREE.Object3D | null>(null)
  const refDragNGyroCameraParent = useRef<THREE.Object3D | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const cameraProps = {
    up: new THREE.Vector3(0, 2, 0),
    near: 0.0001,
    far: 10000,
    scale: 27,
    makeDefault: true
  }

  const holderProps = {}

  useEffect(() => {
    setShowBrokenLegPrompt(props.inputInitialised)

    setCurrentCameraParent(refBlankCameraParent.current as THREE.Object3D)
  }, [])

  useEffect(() => {
    if (roomReady === false) return

    canvasRef.current?.addEventListener('mousedown', (event: MouseEvent) => {
      startPointerPos.set(event.clientX, event.clientY)
    })

    canvasRef.current?.addEventListener('mouseup', (event: MouseEvent) => {
      if (Math.abs(event.clientX - startPointerPos.x) > 5 ||
        Math.abs(event.clientY - startPointerPos.y) > 5) {
        return
      }

      setUserInteracted(true)
    })

    canvasRef.current?.addEventListener('touchstart', (event: TouchEvent) => {
      if (event.touches == null || event.touches[0] == null) return
      startPointerPos.set(event.touches[0].clientX, event.touches[0].clientY)
    })

    canvasRef.current?.addEventListener('touchend', (event: TouchEvent) => {
      if (event.touches == null || event.touches[0] == null) return
      if (Math.abs(event.touches[0].clientX - startPointerPos.x) > 5 ||
        Math.abs(event.touches[0].clientY - startPointerPos.y) > 5) {
        return
      }

      setUserInteracted(true)
    })
  }, [roomReady])

  // @ts-ignore
  const canCurrentlyPan = () => {
    return canDragPan
  }

  const showMoveThisRender = () => {
    return showMoveView === true &&
      (!showBrokenLegPrompt &&
       !showEndPrompt &&
       !showGoodLuckPrompt)
  }

  useEffect(() => {
    if (userInteracted && animatingRoom === false) {
      if (animatingRoom) return
      if (roomReady === false) return

      if (!canMove) {
        setCanMove(true)
      } else {
        setRoomAnimationState(roomAnimationState >= 4 ? 1 : roomAnimationState + 1)
      }
    }
    setUserInteracted(false)
  }, [userInteracted])

  useEffect(() => {
    if (refBlankCameraParent.current == null ||
        refDragCameraParent.current == null ||
        refGyroCameraParent.current == null ||
        refDragNGyroCameraParent.current == null) return

    switch (props.chosenInputMethod) {
      case 'Drag':
        setCurrentCameraParent(refDragCameraParent.current as THREE.Object3D)
        break
      case 'Gyro':
        setCurrentCameraParent(refGyroCameraParent.current as THREE.Object3D)
        break
      case 'Both':
        setCurrentCameraParent(refDragNGyroCameraParent.current as THREE.Object3D)
        break
    }
  },
  [cameraModeInitialised,
    refBlankCameraParent.current,
    refDragCameraParent.current,
    refGyroCameraParent.current,
    refDragNGyroCameraParent.current,
    props.chosenInputMethod])

  useEffect(() => {
    setShowBrokenLegPrompt(props.inputInitialised)
  }, [props.inputInitialised])

  const renderBrokenLegPopup = () => {
    return (
      <Fragment>
        {showBrokenLegPrompt === true &&
        <GenericPopup title='Broken leg mystery'
                      description='Matt broke his leg. Help find out why. Collect the hazards to solve the mystery.'
                      buttonText="Let's Go"
                      popupImage='/images/Broken_leg.svg'
                      callback={() => {
                        setShowBrokenLegPrompt(false)
                        setUserInteracted(true)
                      }}/>}
      </Fragment>
    )
  }

  const renderGoodLuck = () => {
    return (
      <Fragment>
        {showEndPrompt === false && showGoodLuckPrompt === true &&
        <GenericPopup title='Good Luck!'
                      description='No Hazards to find here yet. But you can look and walk around in this room'
                      buttonText="Tap to continue"
                      popupImage='/images/Detective.svg'
                      callback={() => {
                        setShowGoodLuckPrompt(false)
                      }}/>}
      </Fragment>
    )
  }

  const renderEndPopup = () => {
    return (
      <Fragment>
        {showEndPrompt === true &&
        <GenericPopup title='Well Done!'
                      description='You have completed this level.'
                      buttonText='Replay'
                      popupImage='/images/Shield_complete.svg'
                      extraButtons={[{
                        text: 'exit',
                        callback: () => {
                          document.dispatchEvent(new Event('reset_application'))
                        }
                      }]}
                      callback={() => {
                        setShowEndPrompt(false)
                      }}/>}
      </Fragment>
    )
  }

  return (
    <Fragment>
      <div className={'w-full h-body relative'}>
        <Canvas ref={canvasRef}
                id={'mainCanvas'}
                className={'viewer'}
                dpr={[1, 2]}>

          <Suspense fallback={null}>
            <HospitalRoom cameraTrackCallback={(cameraTrack) => setCameraTrackNode(cameraTrack)}
                          animationState={roomAnimationState}

                          startedMoveAnimationCallback={(animationNumber) => {
                            setAnimatingRoom(true)
                            if (animationNumber === 0 || animationNumber === 4) setTimeout(() => { setShowGoodLuckPrompt(true) }, 750)
                            if (animationNumber === 4) setTimeout(() => { setShowEndPrompt(true) }, 750)
                          }}
                          finishedMoveAnimationCallback={(finishedClipNumber:number) => {
                            setMoveCount(finishedClipNumber === 4 ? 0 : finishedClipNumber)

                            if (animatingRoom) setAnimatingRoom(false)
                            if (!canDragPan && finishedClipNumber === 0) {
                              setCanDragPan(true)
                            }
                            if (finishedClipNumber === 0) setShowMoveView(true)
                          }}
                          canPlay={canMove}/>
            {roomReady === false && setRoomReady(true)}
            <Reparent targetParent={cameraTrackNode as THREE.Object3D}>
              <Reparent targetParent={currentCameraParent as THREE.Object3D}>
                <FOVCamera {...props}
                           ref={camera}
                           holderProps={holderProps}
                           cameraProps={cameraProps}/>
              </Reparent>
              <object3D ref={refBlankCameraParent}/>
              <group>
                <PanHandler enabled={canCurrentlyPan()}
                            dragSensitivity={dragSensitivityOverride}>
                  <object3D ref={refDragCameraParent}/>
                </PanHandler>
                <MotionControls>
                  <object3D ref={refGyroCameraParent}/>
                </MotionControls>

                <PanHandler enabled={canCurrentlyPan()}
                            dragSensitivity={dragSensitivityOverride}>
                  <MotionControls>
                    <object3D ref={refDragNGyroCameraParent}/>
                  </MotionControls>
                </PanHandler>
              </group>
            </Reparent>
          </Suspense>
          <ThreeJSObserve />
          <ForceHierarchyUpdate />
        </Canvas>

      </div>
      {cameraTrackNode !== null && renderBrokenLegPopup()}
      {renderGoodLuck()}
      {renderEndPopup()}
      {showMoveThisRender() && <MoveView totalCount={3}
                                         currentCount={moveCount}
                                         callback={() => {
                                           setUserInteracted(true)
                                         }}/>}
      {(moveCount === 0 && showGoodLuckPrompt === false && showGoodLuckPrompt === false) &&
      <LookHowPopup chosenInputMethod={props.chosenInputMethod === 'Drag' ? 'Drag' : 'Gyro'}/>}
    </Fragment>
  )
}

export default HospitalRoomScene
